import { MutableRefObject, useCallback, useEffect, useRef, useState } from 'react';
import { OnChangeHandler } from '../types';

export const useChangeEvent = (
  value: string,
  onBlur: () => void,
  onChange: OnChangeHandler<string>,
  inputRef: MutableRefObject<HTMLInputElement | null>,
  multiline: boolean,
  debounceMs = 500,
) => {
  const [innerValue, setInnerValue] = useState<string>(value);
  const [innerError, setError] = useState<string | undefined>();
  const innerValueRef = useRef(value);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const hasPendingChangeRef = useRef(false);

  const emitChange = useCallback(
    (nextValue: string) => {
      const result = onChange?.(nextValue);

      Promise.resolve(result).then((changeResult) => {
        const error = changeResult && changeResult.error;
        setError(error || undefined);
      });
    },
    [onChange],
  );

  const abortPendingChange = useCallback(() => {
    if (timeoutRef.current !== undefined) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = undefined;
    }
  }, []);

  const flushPendingChange = useCallback(() => {
    abortPendingChange();

    if (!hasPendingChangeRef.current) {
      return;
    }

    hasPendingChangeRef.current = false;
    emitChange(innerValueRef.current);
  }, [abortPendingChange, emitChange]);

  const onBlurInner = useCallback(() => {
    flushPendingChange();
    inputRef.current?.scrollTo?.(0, 0);
    onBlur?.();
  }, [flushPendingChange, inputRef, onBlur]);

  const onChangeInner = useCallback(
    (nextValue: string) => {
      const normalizedValue = multiline ? nextValue.replace(/[\n\r]/g, '') : nextValue;

      abortPendingChange();
      innerValueRef.current = normalizedValue;
      hasPendingChangeRef.current = true;
      setInnerValue(normalizedValue);

      if (debounceMs <= 0) {
        flushPendingChange();
        return;
      }

      timeoutRef.current = setTimeout(() => {
        timeoutRef.current = undefined;
        hasPendingChangeRef.current = false;
        emitChange(innerValueRef.current);
      }, debounceMs);
    },
    [abortPendingChange, debounceMs, emitChange, flushPendingChange, multiline],
  );

  useEffect(
    () => () => {
      abortPendingChange();
    },
    [abortPendingChange],
  );

  useEffect(() => {
    if (hasPendingChangeRef.current) {
      return;
    }

    innerValueRef.current = value;
    setInnerValue(value);
  }, [value]);

  return {
    onChangeInner,
    onBlurInner,
    innerValue,
    innerError,
  };
};
